import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useIntl } from '@futrli/localisation';

import {
  SortConfig,
  buildSortOptions,
  parseSortOptionValue,
  toSortOptionValue,
  useFilteredData,
  useListFilterPersistence,
  readPersistedFilters
} from '../../../../../utils/list-filters';
import { REPORTS_SORT_COLUMNS, REPORTS_COMPARATORS, DEFAULT_REPORTS_SORT, EXPORTED_PDFS_STORAGE_KEY } from '../../shared/sort-config';
import { ExportedPdfRowItem } from '../types';

const nameAccessor = (item: ExportedPdfRowItem) => item.name;

/**
 * Filter + sort hook for the Exported PDFs tab.
 * The simplest tab — only provides search by name and sort (no additional filters).
 * Pipeline: search by name → sort by created date or name.
 */
export const useExportedPdfsFilter = (data: ExportedPdfRowItem[]) => {
  const intl = useIntl();
  const { organisationId = '' } = useParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const persisted = readPersistedFilters(EXPORTED_PDFS_STORAGE_KEY, organisationId);
    return persisted?.sort ?? DEFAULT_REPORTS_SORT;
  });

  const filters = useMemo(() => ({}), []);

  const handleRestore = useCallback(
    (_restoredFilters: Record<string, never>, restoredSort: SortConfig | undefined) => {
      if (restoredSort) {
        setSortConfig(restoredSort);
      }
    },
    []
  );

  useListFilterPersistence({
    storageKey: EXPORTED_PDFS_STORAGE_KEY,
    organisationId,
    filters,
    sort: sortConfig,
    onRestore: handleRestore
  });

  const sortOptions = useMemo(() => buildSortOptions(REPORTS_SORT_COLUMNS, intl), [intl]);
  const sortValue = useMemo(() => toSortOptionValue(sortConfig), [sortConfig]);

  const handleSortChange = useCallback((value: string) => {
    setSortConfig(parseSortOptionValue(value));
  }, []);

  const searchAccessors = useMemo(() => [nameAccessor], []);

  const filteredData = useFilteredData<ExportedPdfRowItem>({
    allData: data,
    searchTerm,
    searchAccessors,
    filterSteps: [],
    sortConfig,
    comparators: REPORTS_COMPARATORS
  });

  return {
    filteredData,
    searchTerm,
    setSearchTerm,
    sortValue,
    sortOptions,
    handleSortChange
  };
};
